import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import type { IconName } from '@/components/Icon/types'
import { safeCustomElement } from '@/utilities/custom-element'
import type { IconToggleButtonSize, IconToggleButtonType } from './types'

@safeCustomElement('mdc-icon-toggle-button')
export class MdcIconToggleButton extends LitElement {
  @property({ type: Boolean }) checked = false
  @property({ type: Boolean }) disabled = false
  @property({ type: String }) type: IconToggleButtonType = 'primary'
  @property({ type: String }) size: IconToggleButtonSize = 'md'
  @property({ type: String, attribute: 'on-icon' }) onIcon: IconName = 'heart-filled'
  @property({ type: String, attribute: 'off-icon' }) offIcon: IconName = 'heart'

  static styles = css`
		:host {
			display: inline-flex;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-icon-toggle-button {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			border: none;
			border-radius: var(--radius-full, 9999px);
			box-sizing: border-box;
			padding: 0.625rem;
			background: none;
			outline: none;
			transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
		}

		.mdc-icon-toggle-button:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-icon-toggle-button:disabled,
		.mdc-icon-toggle-button[aria-disabled='true'] {
			cursor: not-allowed;
			pointer-events: none;
		}

		.mdc-icon-toggle-button--type-primary {
			color: var(--color-functional-gray-white);
			background-color: rgba(17, 17, 17, 0.5);
			backdrop-filter: blur(20px);
		}

		.mdc-icon-toggle-button--type-primary:hover,
		.mdc-icon-toggle-button--type-primary:focus-visible {
			background-color: var(--color-functional-gray-800);
		}

		.mdc-icon-toggle-button--type-primary:disabled,
		.mdc-icon-toggle-button--type-primary[aria-disabled='true'] {
			color: var(--color-functional-gray-300);
			background-color: var(--color-functional-gray-50);
		}

		.mdc-icon-toggle-button--type-tertiary {
			color: var(--color-functional-gray-black);
		}

		.mdc-icon-toggle-button--type-tertiary:hover,
		.mdc-icon-toggle-button--type-tertiary:focus-visible {
			background-color: var(--color-functional-gray-25);
		}

		.mdc-icon-toggle-button--type-tertiary:disabled,
		.mdc-icon-toggle-button--type-tertiary[aria-disabled='true'] {
			color: var(--color-functional-gray-300);
		}

		.mdc-icon-toggle-button--size-lg {
			width: var(--spacing-250, 2.5rem);
			height: var(--spacing-250, 2.5rem);
		}

		.mdc-icon-toggle-button--size-md {
			width: var(--spacing-200, 2rem);
			height: var(--spacing-200, 2rem);
		}

		.mdc-icon-toggle-button--size-sm {
			width: var(--spacing-150, 1.5rem);
			height: var(--spacing-150, 1.5rem);
			padding: 0.25rem;
		}

		.mdc-icon-toggle-button__icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: var(--spacing-125, 1.25rem);
			height: var(--spacing-125, 1.25rem);
			flex-shrink: 0;
		}

		.mdc-icon-toggle-button__icon > span {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
		}

		.mdc-icon-toggle-button__icon svg {
			width: 100%;
			height: 100%;
			display: block;
		}

		.mdc-icon-toggle-button[aria-pressed='true'] .mdc-icon-toggle-button__icon--on {
			display: inherit;
		}

		.mdc-icon-toggle-button[aria-pressed='true'] .mdc-icon-toggle-button__icon--off {
			display: none;
		}

		.mdc-icon-toggle-button[aria-pressed='false'] .mdc-icon-toggle-button__icon--on {
			display: none;
		}

		.mdc-icon-toggle-button[aria-pressed='false'] .mdc-icon-toggle-button__icon--off {
			display: inherit;
		}
	`

  private handleClick(e: Event) {
    if (this.disabled) {
      e.preventDefault()
      return
    }
    this.checked = !this.checked
    this.dispatchEvent(
      new CustomEvent('click', {
        detail: { checked: this.checked, originalEvent: e },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    const className = `mdc-icon-toggle-button mdc-icon-toggle-button--type-${this.type} mdc-icon-toggle-button--size-${this.size}`

    return html`
			<button
				type="button"
				class=${className}
				aria-pressed=${this.checked ? 'true' : 'false'}
				?disabled=${this.disabled}
				@click=${this.handleClick}
			>
				<span class="mdc-icon-toggle-button__icon mdc-icon-toggle-button__icon--on" aria-hidden="true">
					<mdc-icon name=${this.onIcon}></mdc-icon>
				</span>
				<span class="mdc-icon-toggle-button__icon mdc-icon-toggle-button__icon--off" aria-hidden="true">
					<mdc-icon name=${this.offIcon}></mdc-icon>
				</span>
			</button>
		`
  }
}
